import {
  Rocket, Users, Funnel, CurrencyInr, Buildings,
  ChatCircle, Lightning, ChartLine, Megaphone,
} from '@phosphor-icons/react';

export const DOCS_CATEGORIES = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: Rocket,
    color: '#E04B2A',
    description: 'Set up your account and learn the basics',
    articles: [
      {
        slug: 'welcome',
        title: 'Welcome to Admitrix',
        type: 'overview',
        readTime: '3 min',
        content: [
          { type: 'paragraph', text: 'Admitrix is an admissions operating system built for Indian education — coaching classes, schools, tuition centres, and education franchises. It replaces spreadsheets, WhatsApp groups, and disconnected tools with one unified platform for managing leads, admissions, fees, communication, and analytics.' },
          { type: 'heading', level: 2, text: 'What can you do with Admitrix?', id: 'what-can-you-do' },
          { type: 'list', items: [
            'Capture leads from Meta Ads, Google, WhatsApp, walk-ins, and 10+ other sources',
            'Track every inquiry through a visual admissions pipeline',
            'Generate invoices and collect fees via UPI, cash, bank transfer, or card',
            'Accept online payments via Razorpay (UPI, cards, netbanking)',
            'Send WhatsApp messages, SMS, and emails directly from the CRM',
            'Automate lead assignment, follow-up reminders, and stage transitions',
            'Manage multiple branches from a single dashboard',
            'Schedule demos with Google Calendar integration',
            'Transfer leads between branches with full history',
            'Get real-time notifications for lead assignments, payments, and stage changes',
            'Get real-time analytics on source ROI, counselor performance, and revenue',
            'Import/export leads via CSV',
            'Set up webhook integrations for Meta Lead Ads and website forms',
          ]},
          { type: 'heading', level: 2, text: 'Who is this for?', id: 'who-is-this-for' },
          { type: 'paragraph', text: 'Admitrix is designed for three primary roles:' },
          { type: 'list', items: [
            'Institute Admins — Full control over branches, settings, team, and data',
            'Branch Managers — Manage leads, fees, and team for their specific branch',
            'Counselors — Handle assigned leads, follow-ups, and communication',
          ]},
          { type: 'heading', level: 2, text: 'How to use these docs', id: 'how-to-use' },
          { type: 'paragraph', text: 'Start with the Getting Started guides to set up your account. Then explore feature-specific sections based on what you need. Each feature has an overview page (great for understanding what it does) and detailed step-by-step guides (for when you\'re ready to use it).' },
          { type: 'tip', text: 'If you\'re evaluating Admitrix, read the overview pages first — they explain what each feature does and who it\'s for. If you\'re an active user, jump straight to the step-by-step guides.' },
        ],
      },
      {
        slug: 'first-login',
        title: 'First Login & Initial Setup',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'This guide walks you through your first login and the essential setup steps to get your Admitrix account ready for your team.' },
          { type: 'heading', level: 2, text: 'Logging in', id: 'logging-in' },
          { type: 'steps', items: [
            'Navigate to your Admitrix URL and click "Sign in"',
            'Enter the email and password provided by your administrator',
            'You\'ll land on the Dashboard — this is your home base showing real-time KPIs',
          ]},
          { type: 'heading', level: 2, text: 'Essential first steps', id: 'essential-first-steps' },
          { type: 'paragraph', text: 'After your first login, complete these setup tasks in order:' },
          { type: 'steps', items: [
            'Update your profile — Go to Settings and set your name, role, and contact details',
            'Review your branch — Check that your branch name, address, and courses are correct',
            'Explore the demo data — Admitrix comes pre-loaded with sample leads, invoices, and pipeline data so you can see every feature in action before adding real data',
          ]},
          { type: 'tip', text: 'The demo data is clearly marked and can be cleared from Settings once you\'re ready to go live with real data.' },
          { type: 'heading', level: 2, text: 'Understanding the layout', id: 'understanding-layout' },
          { type: 'paragraph', text: 'The Admitrix interface has three main areas:' },
          { type: 'list', items: [
            'Left sidebar — Navigation to all main sections (Dashboard, Pipeline, Leads, Fees, etc.). Collapsible for more screen space.',
            'Top header — Branch selector (if multi-branch), search, and your profile menu',
            'Main content area — Where all the action happens. Changes based on which section you\'re viewing.',
          ]},
          { type: 'warning', text: 'If you\'re a branch manager or counselor, you\'ll only see sections and data relevant to your role and branch. This is by design — Admitrix uses role-based access control.' },
        ],
      },
      {
        slug: 'adding-branch',
        title: 'Adding Your First Branch',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Branches are the foundation of Admitrix. Every lead, invoice, and team member belongs to a branch. Even single-location institutes need one branch configured.' },
          { type: 'heading', level: 2, text: 'Creating a branch', id: 'creating-branch' },
          { type: 'steps', items: [
            'Navigate to Branches from the sidebar',
            'Click "Add Branch" in the top-right corner',
            'Fill in the branch details: name, address, city, phone number, and email',
            'Select which courses this branch offers (you can add courses in the Courses section first)',
            'Click "Create Branch" to save',
          ]},
          { type: 'tip', text: 'Use descriptive branch names that your team will recognize — e.g., "Kothrud Centre" or "Andheri West Branch" rather than "Branch 1".' },
          { type: 'heading', level: 2, text: 'Branch settings', id: 'branch-settings' },
          { type: 'paragraph', text: 'Each branch can have its own:' },
          { type: 'list', items: [
            'Course offerings and fee structures',
            'Team members with role-based access',
            'Pipeline stage configuration',
            'Communication templates',
          ]},
          { type: 'heading', level: 2, text: 'Next steps', id: 'next-steps' },
          { type: 'paragraph', text: 'Once your branch is set up, add courses, invite your team, and start capturing leads. Check the Lead Management section for how to bring in your first inquiries.' },
        ],
      },
      {
        slug: 'inviting-team',
        title: 'Inviting Your Team',
        type: 'detail',
        readTime: '3 min',
        content: [
          { type: 'paragraph', text: 'Add your counselors, branch managers, and staff so they can start working with leads and students.' },
          { type: 'heading', level: 2, text: 'Adding team members', id: 'adding-team' },
          { type: 'steps', items: [
            'Go to Settings from the sidebar',
            'Select the "Team" tab',
            'Click "Invite Member"',
            'Enter their name, email, and select their role',
            'Assign them to a branch',
            'Click "Send Invite" — they\'ll receive login credentials via email',
          ]},
          { type: 'heading', level: 2, text: 'Understanding roles', id: 'understanding-roles' },
          { type: 'paragraph', text: 'Admitrix has 7 roles. You can view the full Roles & Permissions table in Settings > Team.' },
          { type: 'list', items: [
            'Super Admin — Full access to everything. Can manage all branches, settings, team members, and system configuration.',
            'Admin — Can manage most settings and view all branches, but cannot modify super admin accounts.',
            'Branch Manager — Full access within their assigned branch. Can manage leads, fees, team, and reports for that branch.',
            'Counselor — Can view and manage leads assigned to them, send messages, and log activities.',
            'Front Desk — Can create leads, record walk-ins, and handle basic inquiries. Cannot view reports or change settings.',
            'Finance — Access to fee collection, invoices, payment recording, and revenue reports. Cannot manage leads or team.',
            'Faculty — View-only access to student lists and batch schedules. Cannot access fees, leads, or settings.',
          ]},
          { type: 'warning', text: 'Choose roles carefully. A counselor cannot see other counselors\' leads, and a branch manager cannot access other branches. This is intentional for data privacy and team focus.' },
        ],
      },
      {
        slug: 'password-reset',
        title: 'Resetting Your Password',
        type: 'detail',
        readTime: '2 min',
        content: [
          { type: 'paragraph', text: 'If you\'ve forgotten your password, Admitrix provides a self-service reset flow via email.' },
          { type: 'heading', level: 2, text: 'Requesting a reset', id: 'requesting-reset' },
          { type: 'steps', items: [
            'Go to the login page and click "Forgot password?"',
            'Enter your registered email address',
            'Check your inbox for a reset link (valid for 1 hour)',
            'Click the link and set a new password (minimum 6 characters)',
            'Sign in with your new password',
          ]},
          { type: 'tip', text: 'If you don\'t receive the email, check your spam folder. If your institute hasn\'t configured email (SendGrid), ask your admin to reset your password manually from Settings.' },
        ],
      },
      {
        slug: 'notifications',
        title: 'Notifications Center',
        type: 'detail',
        readTime: '2 min',
        content: [
          { type: 'paragraph', text: 'Admitrix keeps you informed with real-time in-app notifications. The bell icon in the header shows your unread notification count.' },
          { type: 'heading', level: 2, text: 'What triggers notifications', id: 'triggers' },
          { type: 'list', items: [
            'A new lead is assigned to you',
            'A lead you\'re managing changes stage',
            'A payment is received on one of your leads',
            'A lead is transferred to you from another branch',
            'A follow-up is overdue',
          ]},
          { type: 'heading', level: 2, text: 'Managing notifications', id: 'managing' },
          { type: 'paragraph', text: 'Click the bell icon to see your recent notifications. Click any notification to navigate to the relevant page and mark it as read. Use "Mark all as read" to clear the badge. Notifications refresh automatically every 30 seconds.' },
        ],
      },
      {
        slug: 'demo-walkthrough',
        title: 'Exploring the Demo Data',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'Admitrix comes pre-loaded with realistic demo data so you can explore every feature before adding your own students and leads.' },
          { type: 'heading', level: 2, text: 'What\'s included in the demo', id: 'whats-included' },
          { type: 'list', items: [
            'Sample branches with addresses across multiple cities',
            'Demo leads at various pipeline stages (new inquiry through enrolled)',
            'Sample invoices with partial payments and outstanding balances',
            'Pre-configured automation rules',
            'Counselor accounts with assigned leads',
          ]},
          { type: 'heading', level: 2, text: 'Guided exploration', id: 'guided-exploration' },
          { type: 'steps', items: [
            'Dashboard — Check the KPI cards at the top for total leads, conversion rate, revenue collected, and outstanding fees. The charts below show source breakdown and counselor performance.',
            'Pipeline — Click Pipeline in the sidebar to see the Kanban board. Try dragging a lead card from one stage to another.',
            'Leads — Open the Leads section to see the table view. Click any lead to see their full profile, activity timeline, and communication history.',
            'Fees — Check the Fees section for sample invoices. Notice how partial payments are tracked and outstanding balances are calculated automatically.',
            'Reports — Explore the Reports section for source ROI charts, counselor leaderboards, and revenue breakdowns.',
          ]},
          { type: 'tip', text: 'The demo data is designed to showcase realistic Indian education scenarios — coaching class inquiries, entrance exam prep courses, and multi-branch operations typical of education franchises.' },
        ],
      },
    ],
  },
  {
    id: 'lead-management',
    title: 'Lead Management',
    icon: Users,
    color: '#2B6EF0',
    description: 'Capture, score, assign, and manage student leads',
    articles: [
      {
        slug: 'overview',
        title: 'Lead Management Overview',
        type: 'overview',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Lead management is the core of Admitrix. Every student journey starts as a lead — an inquiry from a parent, a form submission from an ad campaign, or a walk-in at your centre. Admitrix captures these leads, scores them, assigns them to counselors, and tracks every interaction until enrollment.' },
          { type: 'heading', level: 2, text: 'Key capabilities', id: 'key-capabilities' },
          { type: 'list', items: [
            'Multi-source capture — Ingest leads from Meta Ads, Google Ads, WhatsApp, website forms, walk-ins, referrals, Just Dial, and more',
            'Parent-student profiles — Dual-contact records with parent phone, student name, board, target exam, and preferred batch',
            'Lead scoring — Automatic prioritization based on source quality, engagement, and stage progression',
            'Smart assignment — Round-robin or rule-based distribution to counselors',
            'Follow-up tracking — Automated reminders with overdue alerts',
            'Bulk operations — Import from Excel/CSV, export filtered lists, batch updates',
            'Full marketing attribution: campaign, ad group, UTMs, GCLID, FBCLID',
            'Smart duplicate detection with multi-touchpoint history',
          ]},
          { type: 'heading', level: 2, text: 'Who uses this', id: 'who-uses-this' },
          { type: 'list', items: [
            'Counselors — Day-to-day lead follow-up, activity logging, communication',
            'Branch Managers — Monitor team performance, reassign leads, review pipeline health',
            'Admins — Configure lead sources, scoring rules, and assignment logic',
          ]},
        ],
      },
      {
        slug: 'capturing-leads',
        title: 'Capturing Leads',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'Leads can enter Admitrix through manual entry, bulk import, or automatic capture from integrated sources.' },
          { type: 'heading', level: 2, text: 'Manual lead entry', id: 'manual-entry' },
          { type: 'steps', items: [
            'Click the "Add Lead" button on the Leads page or Pipeline view',
            'Fill in the required fields: student name, parent name, parent phone number',
            'Add optional details: email, course interest, source, branch, preferred batch timing',
            'Select the initial pipeline stage (defaults to "New Inquiry")',
            'Click "Create Lead" to save',
          ]},
          { type: 'heading', level: 2, text: 'Bulk import', id: 'bulk-import' },
          { type: 'steps', items: [
            'Go to Leads and click "Import" in the toolbar',
            'Download the CSV template to see the expected column format',
            'Fill in your data — one row per lead, matching the column headers',
            'Upload the file and review the preview',
            'Map any columns that didn\'t auto-match',
            'Click "Import" — leads are created and assigned based on your default rules',
          ]},
          { type: 'tip', text: 'When importing, duplicate phone numbers are detected automatically. You can choose to skip duplicates, update existing records, or create new entries.' },
          { type: 'heading', level: 2, text: 'Lead sources', id: 'lead-sources' },
          { type: 'paragraph', text: 'Tracking where leads come from is essential for measuring marketing ROI. Admitrix supports these source categories:' },
          { type: 'list', items: [
            'Meta Ads (Facebook/Instagram)',
            'Google Ads',
            'WhatsApp inquiry',
            'Website form',
            'Walk-in',
            'Referral',
            'Just Dial',
            'Phone call',
            'Event/seminar',
            'Other (custom)',
          ]},
          { type: 'warning', text: 'Always set the correct source when adding leads manually. This data feeds directly into your Source ROI reports — inaccurate sources mean inaccurate analytics.' },
          { type: 'heading', level: 2, text: 'Attribution data', id: 'attribution' },
          { type: 'paragraph', text: 'When leads come in via Meta Lead Ads or your website, attribution data (campaign, ad group, UTMs, GCLID/FBCLID) is captured automatically. This data appears on the lead profile and feeds the Campaign Performance report.' },
          { type: 'paragraph', text: 'To track campaigns properly, create a campaign in Admitrix for every paid campaign you run. Match the external campaign ID to your ad platform\'s campaign ID so leads link automatically.' },
        ],
      },
      {
        slug: 'lead-scoring',
        title: 'Lead Scoring & Assignment',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'Lead scoring helps your team focus on the most promising inquiries. Admitrix scores leads based on configurable criteria and automatically assigns them to the right counselor.' },
          { type: 'heading', level: 2, text: 'How scoring works', id: 'how-scoring-works' },
          { type: 'paragraph', text: 'Each lead receives a score from 0-100 based on weighted factors:' },
          { type: 'list', items: [
            'Source quality — Referrals and walk-ins typically score higher than cold ad clicks',
            'Engagement — Leads who respond to messages, attend demos, or visit your centre get score boosts',
            'Stage progression — Moving forward in the pipeline increases the score',
            'Recency — Recent activity scores higher than stale leads',
          ]},
          { type: 'heading', level: 2, text: 'Assignment methods', id: 'assignment-methods' },
          { type: 'list', items: [
            'Round-robin — Leads distributed evenly across available counselors in the branch',
            'Rule-based — Assign based on source, course interest, or location (e.g., all JEE leads go to Counselor A)',
            'Manual — Admin or branch manager assigns leads by hand',
          ]},
          { type: 'tip', text: 'Combine scoring with automation: create a rule that auto-assigns high-score leads (80+) to your best-performing counselor for maximum conversion.' },
        ],
      },
      {
        slug: 'lead-profiles',
        title: 'Managing Lead Profiles',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Every lead in Admitrix has a detailed profile page showing their complete journey — from first inquiry to enrollment.' },
          { type: 'heading', level: 2, text: 'Profile sections', id: 'profile-sections' },
          { type: 'list', items: [
            'Contact details — Student name, parent name, phone, email, address',
            'Academic info — Board, class, target exam, preferred batch timing',
            'Pipeline status — Current stage, assigned counselor, branch',
            'Activity timeline — Every call, message, note, stage change, and system event in chronological order',
            'Communication log — All WhatsApp, SMS, and email messages sent to this lead',
            'Invoices — Any fee invoices generated for this lead',
            'Tags — Custom labels for segmentation (e.g., "hot lead", "scholarship candidate", "parent-referred")',
          ]},
          { type: 'heading', level: 2, text: 'Updating lead information', id: 'updating-info' },
          { type: 'steps', items: [
            'Click on any lead from the Leads table or Pipeline board to open their profile',
            'Click the edit icon next to any field to modify it',
            'Changes are saved automatically and logged in the activity timeline',
            'Add notes using the note input at the bottom of the timeline — these are visible to all team members with access',
          ]},
          { type: 'warning', text: 'All changes to lead profiles are logged in the audit trail. This means every edit is traceable — important for compliance and accountability.' },
        ],
      },
      {
        slug: 'bulk-import',
        title: 'Importing Leads in Bulk',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Moving to Admitrix from spreadsheets or another CRM? Bulk import lets you bring in hundreds or thousands of leads at once.' },
          { type: 'heading', level: 2, text: 'Preparing your data', id: 'preparing-data' },
          { type: 'steps', items: [
            'Download the CSV template from the Import dialog',
            'Required columns: student_name, parent_name, parent_phone',
            'Optional columns: email, source, course, branch, stage, notes',
            'Clean your data: remove duplicates, standardize phone numbers (10 digits, no country code)',
            'Save as CSV (UTF-8 encoding for non-English names)',
          ]},
          { type: 'heading', level: 2, text: 'Running the import', id: 'running-import' },
          { type: 'steps', items: [
            'Go to Leads > click "Import"',
            'Upload your CSV file',
            'Review the column mapping — Admitrix auto-detects common column names',
            'Fix any unmapped columns by selecting from the dropdown',
            'Choose duplicate handling: skip, update, or create new',
            'Click "Import" and wait for processing',
            'Review the summary: successful imports, skipped rows, and any errors',
          ]},
          { type: 'tip', text: 'Start with a small test import (10-20 leads) to verify your column mapping before importing your full dataset.' },
        ],
      },
      {
        slug: 'transfer-reassign',
        title: 'Transferring & Reassigning Leads',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'When a student prefers a different branch or a counselor is overloaded, you can transfer leads between branches or reassign them to different counselors — with full history preserved.' },
          { type: 'heading', level: 2, text: 'Transferring to another branch', id: 'transfer-branch' },
          { type: 'steps', items: [
            'Open the lead\'s profile page',
            'Click the "Transfer" button in the header',
            'Select the target branch from the dropdown',
            'Choose a counselor at the new branch, or select "Auto-assign" for round-robin assignment',
            'Add a reason for the transfer (e.g., "Parent prefers Andheri location")',
            'Click "Transfer" — the lead moves instantly with full activity history preserved',
          ]},
          { type: 'heading', level: 2, text: 'Reassigning to another counselor', id: 'reassign-counselor' },
          { type: 'paragraph', text: 'To reassign within the same branch (e.g., balancing workloads):' },
          { type: 'steps', items: [
            'Go to the Leads table view',
            'Select one or more leads using checkboxes',
            'Click "Reassign Counselor" in the bulk actions toolbar',
            'Select the target counselor from the dropdown',
            'Confirm — all selected leads are reassigned',
          ]},
          { type: 'heading', level: 2, text: 'Bulk transfer', id: 'bulk-transfer' },
          { type: 'paragraph', text: 'You can also transfer multiple leads to another branch at once using the "Transfer to Branch" bulk action in the Leads table. This is useful when opening a new branch or restructuring territories.' },
          { type: 'tip', text: 'Every transfer is logged in the lead\'s activity timeline and the audit log, including who transferred it and why. The new counselor also receives a notification.' },
        ],
      },
    ],
  },
  {
    id: 'admissions-pipeline',
    title: 'Admissions Pipeline',
    icon: Funnel,
    color: '#8B5CF6',
    description: 'Visual Kanban board for tracking admissions progress',
    articles: [
      {
        slug: 'overview',
        title: 'Admissions Pipeline Overview',
        type: 'overview',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'The admissions pipeline is a visual Kanban board that shows every lead in your funnel. Each column represents a stage in your admissions process, and lead cards move left to right as they progress toward enrollment.' },
          { type: 'heading', level: 2, text: 'Default pipeline stages', id: 'default-stages' },
          { type: 'list', items: [
            'New Inquiry — Fresh leads that haven\'t been contacted yet',
            'Contacted — First outreach made, awaiting response',
            'Qualified — Confirmed interest, meets basic criteria',
            'Demo Scheduled — Demo class or campus visit booked',
            'Demo Attended — Student attended the demo/visit',
            'Application — Application form submitted',
            'Fee Pending — Admitted but payment not yet received',
            'Enrolled — Fee paid, student is enrolled',
            'Lost — Lead didn\'t convert (with reason tracking)',
            'On Hold — Temporarily paused (e.g., waiting for exam results)',
          ]},
          { type: 'heading', level: 2, text: 'Key capabilities', id: 'key-capabilities' },
          { type: 'list', items: [
            'Drag-and-drop cards between stages',
            'Filter pipeline by source, counselor, branch, course, or date',
            'Bulk stage changes after events or batch demos',
            'Stage-wise conversion analytics',
            'Activity timeline on each lead card',
          ]},
        ],
      },
      {
        slug: 'setting-up-stages',
        title: 'Setting Up Stages',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'While the default stages work for most institutes, you can customize the pipeline to match your specific admissions process.' },
          { type: 'heading', level: 2, text: 'Customizing stages', id: 'customizing-stages' },
          { type: 'steps', items: [
            'Go to Settings > Pipeline',
            'You\'ll see the current stages in order',
            'To rename a stage: click its name and type the new label',
            'To reorder: drag stages up or down',
            'To add a new stage: click "Add Stage" and position it in the sequence',
            'Save your changes — they apply across all branches',
          ]},
          { type: 'warning', text: 'Removing a stage will move all leads in that stage to the previous one. Always check how many leads are in a stage before removing it.' },
          { type: 'tip', text: 'Keep your pipeline lean — 6-8 stages is ideal. Too many stages create friction for counselors and make the board hard to scan.' },
        ],
      },
      {
        slug: 'moving-leads',
        title: 'Moving Leads Through the Pipeline',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'Moving leads through pipeline stages is the core daily workflow for counselors and branch managers.' },
          { type: 'heading', level: 2, text: 'Drag and drop', id: 'drag-and-drop' },
          { type: 'paragraph', text: 'The simplest way to update a lead\'s stage: click and hold the lead card on the Kanban board, then drag it to the target stage column. The stage change is saved immediately and logged in the lead\'s activity timeline.' },
          { type: 'heading', level: 2, text: 'From the lead profile', id: 'from-profile' },
          { type: 'steps', items: [
            'Open any lead\'s profile page',
            'Click the current stage badge near the top',
            'Select the new stage from the dropdown',
            'Optionally add a note explaining why (e.g., "Demo attended, very interested in JEE batch")',
          ]},
          { type: 'heading', level: 2, text: 'Bulk stage changes', id: 'bulk-changes' },
          { type: 'steps', items: [
            'Go to the Leads table view',
            'Select multiple leads using the checkboxes',
            'Click "Bulk Actions" in the toolbar',
            'Choose "Change Stage" and select the target stage',
            'Confirm — all selected leads move at once',
          ]},
          { type: 'tip', text: 'After a batch demo event, use bulk stage change to move all attendees from "Demo Scheduled" to "Demo Attended" in one action.' },
        ],
      },
      {
        slug: 'pipeline-analytics',
        title: 'Pipeline Analytics',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Pipeline analytics show you where leads are getting stuck and how efficiently your funnel converts inquiries to enrollments.' },
          { type: 'heading', level: 2, text: 'Key metrics', id: 'key-metrics' },
          { type: 'list', items: [
            'Stage distribution — How many leads are in each stage right now',
            'Conversion rate — Percentage of leads moving from one stage to the next',
            'Average time in stage — How long leads sit at each step before progressing',
            'Drop-off points — Which stages lose the most leads',
            'Pipeline velocity — Average time from new inquiry to enrollment',
          ]},
          { type: 'heading', level: 2, text: 'Using analytics to improve', id: 'using-analytics' },
          { type: 'paragraph', text: 'Look for these patterns:' },
          { type: 'list', items: [
            'High drop-off at "Demo Scheduled" → demos aren\'t being confirmed; add automated reminders',
            'Long time in "Fee Pending" → payment friction; send WhatsApp payment reminders',
            'Low conversion from "Contacted" to "Qualified" → scripting or training issue; review counselor talk tracks',
          ]},
          { type: 'tip', text: 'Review pipeline analytics weekly with your team. Focus on the single biggest bottleneck rather than trying to optimize everything at once.' },
        ],
      },
    ],
  },
  {
    id: 'fee-collection',
    title: 'Fee Collection',
    icon: CurrencyInr,
    color: '#E5A100',
    description: 'Invoicing, payments, and outstanding tracking',
    articles: [
      {
        slug: 'overview',
        title: 'Fee Collection Overview',
        type: 'overview',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Admitrix handles the full fee lifecycle — from generating invoices to collecting payments to tracking outstanding dues. No more Excel fee registers or manual reconciliation.' },
          { type: 'heading', level: 2, text: 'Key capabilities', id: 'key-capabilities' },
          { type: 'list', items: [
            'Professional invoice generation with course details, discounts, and tax breakdowns',
            'Partial payment tracking — accept installments and see the full payment history per student',
            'Multi-mode payment recording: UPI, cash, cheque, bank transfer, card',
            'Real-time outstanding dashboard by branch, course, and student',
            'Automated payment reminders via WhatsApp and SMS',
            'Receipt generation and fee reports for accounting',
            'Online payment collection via Razorpay (UPI, cards, netbanking, wallets)',
            'Payment receipt emails sent automatically after every payment',
            'One-click payment reminders via WhatsApp',
            'CSV export of fee reports',
          ]},
          { type: 'heading', level: 2, text: 'Who uses this', id: 'who-uses-this' },
          { type: 'list', items: [
            'Admins — Set up fee structures, generate reports, reconcile payments',
            'Branch Managers — Monitor collection rates, follow up on outstanding dues',
            'Counselors — Generate invoices for enrolled students, record payments received',
          ]},
        ],
      },
      {
        slug: 'creating-invoices',
        title: 'Creating Invoices',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'Invoices are generated for enrolled students (or leads in the "Fee Pending" stage) to formally request payment.' },
          { type: 'heading', level: 2, text: 'Generating an invoice', id: 'generating-invoice' },
          { type: 'steps', items: [
            'Go to Fees from the sidebar and click "Create Invoice"',
            'Search and select the student/lead',
            'Select the course and fee structure — the amount auto-fills based on your configured fee templates',
            'Apply any discounts (percentage or fixed amount) with a reason note',
            'Set the due date',
            'Click "Generate Invoice" — a professional invoice is created with a unique invoice number',
          ]},
          { type: 'tip', text: 'You can generate invoices directly from a lead\'s profile page too — click the "Generate Invoice" button in their fee section.' },
          { type: 'heading', level: 2, text: 'Invoice details', id: 'invoice-details' },
          { type: 'paragraph', text: 'Each invoice includes: institute name and branch, student and parent details, course name and batch, fee breakdown with discounts, due date, and payment instructions.' },
        ],
      },
      {
        slug: 'recording-payments',
        title: 'Recording Payments',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'When a student or parent makes a payment, record it in Admitrix to keep fee records accurate and up to date.' },
          { type: 'heading', level: 2, text: 'Recording a payment', id: 'recording-payment' },
          { type: 'steps', items: [
            'Find the invoice in Fees or on the student\'s profile',
            'Click "Record Payment"',
            'Enter the amount received (can be partial — doesn\'t need to match the full invoice)',
            'Select the payment mode: UPI, cash, cheque, bank transfer, or card',
            'Add a reference ID if applicable (UPI transaction ID, cheque number, etc.)',
            'Click "Save" — the invoice balance updates automatically',
          ]},
          { type: 'heading', level: 2, text: 'Payment modes', id: 'payment-modes' },
          { type: 'list', items: [
            'UPI — Record with transaction reference ID',
            'Cash — Record with receipt number',
            'Cheque — Record with cheque number and bank name',
            'Bank Transfer — Record with NEFT/RTGS reference',
            'Card — Record with last 4 digits and approval code',
          ]},
          { type: 'warning', text: 'Always record payments on the same day they\'re received. Delayed entries create confusion in outstanding reports and can lead to duplicate payment reminders being sent.' },
        ],
      },
      {
        slug: 'outstanding-dues',
        title: 'Tracking Outstanding Dues',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'The outstanding dashboard gives you a real-time view of all unpaid fees across your institute.' },
          { type: 'heading', level: 2, text: 'Viewing outstanding dues', id: 'viewing-outstanding' },
          { type: 'steps', items: [
            'Go to Fees from the sidebar — the default view shows all invoices',
            'Use the status filter to show only "Pending" or "Partially Paid" invoices',
            'Filter by branch, course, or date range to narrow down',
            'The summary bar at the top shows total outstanding amount',
          ]},
          { type: 'heading', level: 2, text: 'Following up on dues', id: 'following-up' },
          { type: 'paragraph', text: 'For each outstanding invoice, you can:' },
          { type: 'list', items: [
            'Send a WhatsApp payment reminder directly from the invoice',
            'View the payment history to see what\'s already been paid',
            'Add notes about payment commitments or special arrangements',
            'Set up automated reminders (see Payment Reminders guide)',
          ]},
          { type: 'tip', text: 'Review outstanding dues weekly at the branch level. Sort by "oldest first" to prioritize long-overdue payments that need escalation.' },
        ],
      },
      {
        slug: 'payment-reminders',
        title: 'Payment Reminders',
        type: 'detail',
        readTime: '3 min',
        content: [
          { type: 'paragraph', text: 'Automated payment reminders reduce manual follow-up and improve collection rates.' },
          { type: 'heading', level: 2, text: 'Setting up reminders', id: 'setting-up' },
          { type: 'steps', items: [
            'Go to Settings > Automation or the Automation section in the sidebar',
            'Create a new rule with trigger "Invoice overdue"',
            'Set the timing: e.g., send reminder 3 days before due date, on due date, and 7 days after',
            'Choose the channel: WhatsApp, SMS, or both',
            'Select a message template (or create a custom one)',
            'Activate the rule',
          ]},
          { type: 'tip', text: 'WhatsApp reminders have a significantly higher open rate than SMS. Use WhatsApp as the primary channel and SMS as a fallback for parents not on WhatsApp.' },
        ],
      },
      {
        slug: 'online-payments',
        title: 'Online Payments via Razorpay',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Admitrix integrates with Razorpay to accept online payments. Parents can pay via UPI, credit/debit card, netbanking, or wallet — directly from the invoice.' },
          { type: 'heading', level: 2, text: 'How it works', id: 'how-it-works' },
          { type: 'steps', items: [
            'Open an unpaid or partially paid invoice in the Fees section',
            'Click the "Pay Online" button (green, with credit card icon)',
            'A Razorpay checkout popup opens showing the outstanding amount',
            'The payer selects their payment method (UPI, card, netbanking, wallet)',
            'After successful payment, the invoice is automatically updated',
            'A payment receipt email is sent if the lead has an email address',
          ]},
          { type: 'heading', level: 2, text: 'Payment verification', id: 'payment-verification' },
          { type: 'paragraph', text: 'Every online payment is cryptographically verified using Razorpay\'s signature verification. This prevents tampering and ensures only genuine payments are recorded. The payment method (UPI, card, etc.) is automatically detected and logged.' },
          { type: 'heading', level: 2, text: 'Setup', id: 'setup' },
          { type: 'paragraph', text: 'To enable online payments, your admin needs to configure Razorpay API keys in the system settings. You can start with Razorpay test mode to verify the integration before going live.' },
          { type: 'tip', text: 'Online payments are the fastest way to reduce outstanding dues. Share the payment link with parents via WhatsApp for instant collection.' },
        ],
      },
    ],
  },
  {
    id: 'multi-branch',
    title: 'Multi-Branch',
    icon: Buildings,
    color: '#10A882',
    description: 'Managing multiple centres from one dashboard',
    articles: [
      {
        slug: 'overview',
        title: 'Multi-Branch Management Overview',
        type: 'overview',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Whether you run 1 centre or 100 franchises, Admitrix\'s multi-branch system gives you centralized control with branch-level independence. Each branch operates with its own leads, fees, and team — but everything rolls up to a single admin dashboard.' },
          { type: 'heading', level: 2, text: 'Key capabilities', id: 'key-capabilities' },
          { type: 'list', items: [
            'Centralized control panel for all branches',
            'Branch-level analytics and comparison',
            'Role-based access — staff see only their branch data',
            'Cross-branch lead transfers with full history',
            'Unified settings with per-branch overrides',
            'Team management with workload visibility',
          ]},
          { type: 'heading', level: 2, text: 'How it works', id: 'how-it-works' },
          { type: 'paragraph', text: 'Admins see all branches from one dashboard. Branch managers see only their branch. Counselors see only their assigned leads within their branch. This hierarchy is enforced at the API level — not just the UI — so data isolation is guaranteed.' },
        ],
      },
      {
        slug: 'adding-branches',
        title: 'Adding & Configuring Branches',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'Adding a new branch takes minutes. You can start from scratch or clone settings from an existing branch.' },
          { type: 'heading', level: 2, text: 'Creating a new branch', id: 'creating-branch' },
          { type: 'steps', items: [
            'Go to Branches from the sidebar (admin access required)',
            'Click "Add Branch"',
            'Enter branch details: name, address, city, contact number, email',
            'Select courses offered at this branch',
            'Configure fee structures (or use the default from another branch)',
            'Click "Create Branch"',
          ]},
          { type: 'heading', level: 2, text: 'Branch configuration', id: 'branch-config' },
          { type: 'paragraph', text: 'Each branch can independently configure:' },
          { type: 'list', items: [
            'Courses and batches available',
            'Fee structures and discount rules',
            'Team members and their roles',
            'Communication templates',
            'Working hours and response SLAs',
          ]},
          { type: 'tip', text: 'When opening a new franchise location, clone an existing branch\'s settings to get started faster. You can then customize as needed.' },
        ],
      },
      {
        slug: 'roles-access',
        title: 'Role-Based Access Control',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'Admitrix enforces strict role-based access control (RBAC) to ensure data privacy and operational focus. Each team member sees only what they need.' },
          { type: 'heading', level: 2, text: 'Role hierarchy', id: 'role-hierarchy' },
          { type: 'list', items: [
            'Super Admin — Everything. All branches, all settings, all data. Usually the institute owner.',
            'Admin — Most settings and all branches, but cannot modify super admin accounts or critical system settings.',
            'Branch Manager — Full control within their assigned branch: leads, fees, team, reports. Cannot see other branches.',
            'Counselor — Their assigned leads only. Can send messages, log activities, and update lead information. Cannot see other counselors\' leads.',
            'Viewer — Read-only access to dashboards and reports. No edit capabilities.',
          ]},
          { type: 'heading', level: 2, text: 'Assigning roles', id: 'assigning-roles' },
          { type: 'steps', items: [
            'Go to Settings > Team',
            'Click on a team member\'s name',
            'Select their role from the dropdown',
            'Assign them to a branch (for branch manager and counselor roles)',
            'Save — access changes take effect immediately',
          ]},
          { type: 'warning', text: 'RBAC is enforced at the API level, not just the UI. Even if someone inspects the code or modifies the frontend, they cannot access data outside their role permissions.' },
        ],
      },
      {
        slug: 'cross-branch',
        title: 'Cross-Branch Lead Transfers',
        type: 'detail',
        readTime: '3 min',
        content: [
          { type: 'paragraph', text: 'When a student prefers a different branch location, you can transfer their lead without losing any history.' },
          { type: 'heading', level: 2, text: 'Transferring a lead', id: 'transferring' },
          { type: 'steps', items: [
            'Open the lead\'s profile',
            'Click the branch name to open the transfer dialog',
            'Select the target branch',
            'Choose whether to also reassign to a counselor at the new branch',
            'Add a transfer note (e.g., "Parent prefers Andheri location, closer to home")',
            'Confirm — the lead moves to the new branch with full history intact',
          ]},
          { type: 'tip', text: 'The transfer is logged in the activity timeline with the reason note. Both the original and new branch can see the full history.' },
        ],
      },
      {
        slug: 'default-branch',
        title: 'Default Branch & Lead Routing',
        type: 'detail',
        readTime: '3 min',
        content: [
          { type: 'paragraph', text: 'When leads come in via webhooks (Meta Ads, website forms) without a specified branch, they need somewhere to go. The default branch setting ensures these leads are automatically routed and assigned.' },
          { type: 'heading', level: 2, text: 'Setting the default branch', id: 'setting-default' },
          { type: 'steps', items: [
            'Go to Settings (super admin access required)',
            'The default branch can be configured via the API: PUT /api/settings/default-branch',
            'All webhook-captured leads without a branch_id will be routed to this branch',
            'Auto-assignment will then pick a counselor from this branch via round-robin',
          ]},
          { type: 'heading', level: 2, text: 'How routing works', id: 'how-routing-works' },
          { type: 'list', items: [
            'Meta Lead Ad captured → no branch specified → routed to default branch → auto-assigned to counselor',
            'Generic webhook lead with branch_id → goes directly to that branch → auto-assigned',
            'Manual lead creation → branch selected in the form → assigned accordingly',
          ]},
          { type: 'tip', text: 'Set your highest-volume branch as the default. You can always transfer leads to other branches later.' },
        ],
      },
    ],
  },
  {
    id: 'communication',
    title: 'Communication',
    icon: ChatCircle,
    color: '#06B6D4',
    description: 'WhatsApp, SMS, and email messaging',
    articles: [
      {
        slug: 'overview',
        title: 'Communication Hub Overview',
        type: 'overview',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'The communication hub unifies WhatsApp, SMS, and email into a single interface. Every message — sent or received — appears on the lead\'s timeline, so any team member can see the full conversation history.' },
          { type: 'heading', level: 2, text: 'Supported channels', id: 'supported-channels' },
          { type: 'list', items: [
            'WhatsApp — via WhatsApp Business API with QR code pairing. Supports templates, media, and delivery tracking.',
            'SMS — DLT-compliant bulk and individual messaging. Works with Indian telecom regulations.',
            'Email — Professional email templates for formal communications like admission letters and fee receipts.',
          ]},
          { type: 'heading', level: 2, text: 'Key capabilities', id: 'key-capabilities' },
          { type: 'list', items: [
            'Unified inbox — All channels in one view per lead',
            'Template library — 50+ pre-built templates for every stage',
            'Scheduled messaging — Queue messages for the right time',
            'Bulk campaigns — Send to filtered segments',
            'Delivery tracking — Know what was sent, delivered, and read',
            'Scheduled messages — queue messages for delivery at a specific time',
            'Template management — create, edit, and delete custom templates',
            'Bulk campaigns with audience filters (stage, source, branch)',
          ]},
        ],
      },
      {
        slug: 'whatsapp-setup',
        title: 'WhatsApp Setup & QR Pairing',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'Admitrix connects to WhatsApp through QR code pairing, similar to WhatsApp Web. This lets you send and receive messages directly from the CRM.' },
          { type: 'heading', level: 2, text: 'Initial setup', id: 'initial-setup' },
          { type: 'steps', items: [
            'Go to Settings > Communication > WhatsApp',
            'Click "Connect WhatsApp" — a QR code appears on screen',
            'Open WhatsApp on the phone you want to connect',
            'Go to WhatsApp Settings > Linked Devices > Link a Device',
            'Scan the QR code with your phone',
            'Wait for the connection confirmation — status changes to "Connected"',
          ]},
          { type: 'warning', text: 'Use a dedicated phone number for your institute\'s WhatsApp. If you use a personal number, all CRM messages will be sent from that number and your personal chats will be visible.' },
          { type: 'heading', level: 2, text: 'Staying connected', id: 'staying-connected' },
          { type: 'paragraph', text: 'The WhatsApp connection stays active as long as the paired phone has internet access. If the connection drops (phone is off, no internet), Admitrix will show a "Disconnected" status and queue messages until reconnection.' },
          { type: 'tip', text: 'Keep the paired phone connected to WiFi and charging at your centre. Treat it as a dedicated "WhatsApp station" for reliable connectivity.' },
        ],
      },
      {
        slug: 'sending-messages',
        title: 'Sending Messages',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Send messages to leads from their profile page, the communication hub, or as part of bulk campaigns.' },
          { type: 'heading', level: 2, text: 'Individual messages', id: 'individual-messages' },
          { type: 'steps', items: [
            'Open a lead\'s profile',
            'Click the messaging icon or go to the "Communication" tab',
            'Select the channel: WhatsApp, SMS, or Email',
            'Type your message or pick a template',
            'Click "Send" — the message is delivered and logged on the timeline',
          ]},
          { type: 'heading', level: 2, text: 'Quick replies', id: 'quick-replies' },
          { type: 'paragraph', text: 'From the lead list or pipeline view, click the WhatsApp or SMS icon next to any lead for a quick send dialog. This is the fastest way to follow up without opening the full profile.' },
          { type: 'tip', text: 'Use templates for repetitive messages (follow-up, demo invite, fee reminder). Templates save time and ensure consistent, professional communication.' },
        ],
      },
      {
        slug: 'templates',
        title: 'Using Message Templates',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Message templates are pre-written messages with dynamic placeholders that auto-fill with lead data. They ensure consistent communication and save your team from typing the same messages repeatedly.' },
          { type: 'heading', level: 2, text: 'Using a template', id: 'using-template' },
          { type: 'steps', items: [
            'When composing a message, click "Use Template"',
            'Browse by category: inquiry acknowledgment, demo invite, follow-up, fee reminder, enrollment confirmation',
            'Select a template — placeholders like {{student_name}}, {{course}}, and {{branch}} auto-fill',
            'Review the filled message and edit if needed',
            'Send',
          ]},
          { type: 'heading', level: 2, text: 'Creating custom templates', id: 'creating-templates' },
          { type: 'steps', items: [
            'Go to Settings > Communication > Templates',
            'Click "Create Template"',
            'Select the channel (WhatsApp, SMS, or Email) and category',
            'Write your message using placeholders: {{student_name}}, {{parent_name}}, {{course}}, {{branch}}, {{due_amount}}, {{due_date}}',
            'Save — the template is available to all team members',
          ]},
          { type: 'tip', text: 'For WhatsApp Business, templates need to be approved by Meta for bulk/campaign use. Individual messages can use any template without approval.' },
        ],
      },
      {
        slug: 'bulk-campaigns',
        title: 'Bulk Campaigns',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Send targeted messages to groups of leads based on filters like stage, source, course interest, or branch.' },
          { type: 'heading', level: 2, text: 'Creating a campaign', id: 'creating-campaign' },
          { type: 'steps', items: [
            'Go to Communication from the sidebar',
            'Click "New Campaign"',
            'Define your audience using filters: stage, source, branch, course, date range, tags',
            'Preview the recipient count — review the list to make sure it\'s right',
            'Select the channel and template',
            'Choose timing: send now or schedule for later',
            'Click "Send Campaign"',
          ]},
          { type: 'warning', text: 'Double-check your audience filters before sending. There\'s no undo for sent messages. A wrongly targeted campaign can confuse parents and damage trust.' },
          { type: 'heading', level: 2, text: 'Campaign analytics', id: 'campaign-analytics' },
          { type: 'paragraph', text: 'After sending, track delivery rates, read receipts (WhatsApp), and any replies. Campaign performance appears in the Communication reports.' },
        ],
      },
      {
        slug: 'scheduled-messages',
        title: 'Scheduled Messages',
        type: 'detail',
        readTime: '3 min',
        content: [
          { type: 'paragraph', text: 'Schedule messages to be sent at a specific date and time — perfect for morning follow-ups, weekend reminders, or batch-start notifications.' },
          { type: 'heading', level: 2, text: 'Scheduling a message', id: 'scheduling' },
          { type: 'steps', items: [
            'Open the Send Message dialog from Communication or a lead\'s profile',
            'Compose your message as usual',
            'Toggle "Schedule for later" at the bottom of the dialog',
            'Pick the date and time for delivery',
            'Click "Schedule" — the message is queued for the specified time',
          ]},
          { type: 'heading', level: 2, text: 'Managing scheduled messages', id: 'managing' },
          { type: 'paragraph', text: 'Go to Communication > Scheduled tab to see all pending scheduled messages. You can cancel any scheduled message before its delivery time.' },
          { type: 'tip', text: 'Schedule follow-up messages during business hours even if you\'re working late. A 10 AM message feels more professional than a midnight one.' },
        ],
      },
      {
        slug: 'managing-templates',
        title: 'Managing Message Templates',
        type: 'detail',
        readTime: '3 min',
        content: [
          { type: 'paragraph', text: 'Create, edit, and organize your own message templates for consistent communication across your team.' },
          { type: 'heading', level: 2, text: 'Creating a template', id: 'creating' },
          { type: 'steps', items: [
            'Go to Communication > Templates tab',
            'Click "New Template"',
            'Enter a name, select the channel (WhatsApp, SMS, or Email), choose a category',
            'Write the message content using placeholders: {{student_name}}, {{parent_name}}, {{course}}, {{branch}}, {{due_amount}}, {{due_date}}',
            'Save — the template is available to all team members immediately',
          ]},
          { type: 'heading', level: 2, text: 'Editing and deleting', id: 'editing' },
          { type: 'paragraph', text: 'Click the pencil icon on any template to edit it, or the trash icon to delete. Changes apply instantly for all users.' },
          { type: 'warning', text: 'Deleting a template is permanent. If other team members rely on it, consider editing instead of deleting.' },
        ],
      },
    ],
  },
  {
    id: 'automation',
    title: 'Automation',
    icon: Lightning,
    color: '#F97316',
    description: 'Trigger-based workflows and automatic actions',
    articles: [
      {
        slug: 'overview',
        title: 'Automation Engine Overview',
        type: 'overview',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'The automation engine lets you build "if this, then that" rules that run 24/7 across all branches. When a trigger condition is met, Admitrix automatically executes one or more actions — no manual intervention needed.' },
          { type: 'heading', level: 2, text: 'How it works', id: 'how-it-works' },
          { type: 'paragraph', text: 'Each automation rule has three parts:' },
          { type: 'list', items: [
            'Trigger — The event that starts the automation (e.g., new lead created, stage changed)',
            'Conditions — Optional filters that narrow when the rule fires (e.g., only for leads from Meta Ads)',
            'Actions — What happens when the rule fires (e.g., assign to counselor, send WhatsApp message)',
          ]},
          { type: 'heading', level: 2, text: 'Execution model', id: 'execution-model' },
          { type: 'list', items: [
            'Event-driven triggers fire instantly — new lead created, stage changed, tag added',
            'Time-based triggers (no activity, score threshold) are checked every 15 minutes',
            'All executions are logged and visible on the automation detail page, including which leads were affected and what actions were taken',
          ]},
          { type: 'heading', level: 2, text: 'Common use cases', id: 'common-use-cases' },
          { type: 'list', items: [
            'Auto-assign new leads to counselors based on round-robin or rules',
            'Send a WhatsApp welcome message when a new inquiry comes in',
            'Notify the branch manager when a lead is stuck in a stage for too long',
            'Auto-move leads to "Enrolled" when fee payment is recorded',
            'Send payment reminders for overdue invoices',
            'Tag leads that haven\'t been contacted in 48 hours',
          ]},
        ],
      },
      {
        slug: 'creating-rules',
        title: 'Creating Automation Rules',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'Building an automation rule is straightforward — pick a trigger, set conditions, choose actions.' },
          { type: 'heading', level: 2, text: 'Step-by-step', id: 'step-by-step' },
          { type: 'steps', items: [
            'Go to Automations from the sidebar',
            'Click "Create Rule"',
            'Name your rule descriptively (e.g., "Auto-assign Meta leads to Priya")',
            'Select a trigger type (see Trigger Types reference)',
            'Add conditions if needed (e.g., source = "Meta Ads" AND branch = "Kothrud")',
            'Add one or more actions (see Action Types reference)',
            'Test with a dry run — Admitrix shows what would happen without actually executing',
            'Toggle the rule "Active" and save',
          ]},
          { type: 'tip', text: 'Always use the dry run feature when creating new rules. One misconfigured automation can affect hundreds of leads.' },
          { type: 'heading', level: 2, text: 'Monitoring rules', id: 'monitoring' },
          { type: 'paragraph', text: 'Each rule shows an execution log — when it fired, which leads it affected, and what actions were taken. Review these logs regularly to make sure rules are working as expected.' },
        ],
      },
      {
        slug: 'triggers',
        title: 'Trigger Types Reference',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'Triggers are the events that cause automation rules to fire. Admitrix supports six trigger types.' },
          { type: 'heading', level: 2, text: 'Available triggers', id: 'available-triggers' },
          { type: 'list', items: [
            'stage_change — Fires when a lead moves to a specific stage (or any stage). Use for: stage-based notifications, auto-actions on enrollment.',
            'new_lead — Fires when a new lead is created from any source. Use for: welcome messages, auto-assignment, initial tagging.',
            'lead_score_above — Fires when a lead\'s score crosses a threshold. Use for: prioritizing hot leads, escalating to senior counselors.',
            'follow_up_overdue — Fires when a scheduled follow-up is missed. Use for: reminder notifications, manager escalation.',
            'tag_added — Fires when a specific tag is added to a lead. Use for: segment-based actions, custom workflow triggers.',
            'no_activity_days — Fires when a lead has no activity for N days. Use for: re-engagement campaigns, stale lead alerts.',
          ]},
          { type: 'warning', text: 'The no_activity_days trigger checks once daily. It\'s not instant — leads won\'t trigger the action until the next daily check after crossing the threshold.' },
        ],
      },
      {
        slug: 'actions',
        title: 'Action Types Reference',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'Actions are what automation rules do when triggered. You can chain multiple actions in a single rule.' },
          { type: 'heading', level: 2, text: 'Available actions', id: 'available-actions' },
          { type: 'list', items: [
            'send_whatsapp — Send a WhatsApp message using a template. Placeholders auto-fill from lead data.',
            'send_sms — Send an SMS using a DLT-approved template.',
            'send_email — Send an email using a configured template.',
            'assign_counselor — Assign the lead to a specific counselor or use round-robin within the branch.',
            'change_stage — Move the lead to a specified pipeline stage.',
            'add_tag — Add a tag to the lead for segmentation (e.g., "hot-lead", "needs-callback").',
            'add_note — Add an automated note to the lead\'s activity timeline.',
          ]},
          { type: 'heading', level: 2, text: 'Action ordering', id: 'action-ordering' },
          { type: 'paragraph', text: 'When a rule has multiple actions, they execute in order. This matters — for example, assign the counselor first, then send the notification message (so the message includes the counselor\'s name).' },
          { type: 'tip', text: 'Keep rules focused: one trigger, one clear purpose. It\'s better to have 5 simple rules than 1 complex rule with 10 conditions and 8 actions.' },
        ],
      },
      {
        slug: 'workflow-templates',
        title: 'Workflow Templates',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Admitrix includes pre-built automation workflows for common scenarios. These are ready-to-use recipes that you can activate with minimal configuration.' },
          { type: 'heading', level: 2, text: 'Available templates', id: 'available-templates' },
          { type: 'list', items: [
            'New Lead Welcome — Auto-assign + send WhatsApp welcome message for every new inquiry',
            'Demo Reminder — Send reminder 24 hours before a scheduled demo',
            'Stale Lead Alert — Notify counselor when a lead has no activity for 3 days',
            'Fee Reminder Sequence — 3-stage payment reminder: 3 days before, on due date, 7 days after',
            'Manager Escalation — Alert branch manager when a lead is stuck in "Contacted" for 5+ days',
            'Enrollment Celebration — Send congratulations message when a lead moves to "Enrolled"',
          ]},
          { type: 'heading', level: 2, text: 'Using a template', id: 'using-template' },
          { type: 'steps', items: [
            'Go to Automations',
            'Click "Templates" tab',
            'Select a template to preview its trigger, conditions, and actions',
            'Click "Use Template" — it creates a new rule pre-filled with the template configuration',
            'Customize any settings (message templates, timing, conditions)',
            'Activate and save',
          ]},
          { type: 'tip', text: 'Start with the "New Lead Welcome" and "Fee Reminder Sequence" templates — these two alone can dramatically improve response time and collection rates.' },
        ],
      },
    ],
  },
  {
    id: 'analytics',
    title: 'Analytics & Reports',
    icon: ChartLine,
    color: '#E04B2A',
    description: 'Dashboards, reports, and data exports',
    articles: [
      {
        slug: 'overview',
        title: 'Analytics & Reports Overview',
        type: 'overview',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Admitrix tracks every action — lead created, stage changed, payment received, message sent — and turns it into actionable analytics. No manual data entry, no waiting for month-end reports.' },
          { type: 'heading', level: 2, text: 'Report categories', id: 'report-categories' },
          { type: 'list', items: [
            'Source ROI — Which marketing channels bring the best leads and highest conversions',
            'Counselor Performance — Conversion rates, response times, and follow-up compliance per team member',
            'Pipeline Analytics — Stage distribution, conversion rates, velocity, and bottlenecks',
            'Revenue Reports — Collections, outstanding, revenue by branch/course/period',
            'Comparative Reports — This month vs last, this quarter vs same quarter last year',
            'Campaign performance — CPL, CPE, ROAS per campaign with funnel breakdown',
            'Platform comparison — Meta vs Google vs walk-in vs referral',
            'Out-of-location lead detection',
          ]},
          { type: 'heading', level: 2, text: 'Real-time data', id: 'real-time' },
          { type: 'paragraph', text: 'All reports reflect real-time data. When a payment is recorded or a lead moves stages, reports update instantly. There\'s no batch processing or overnight refresh — what you see is what\'s happening now.' },
        ],
      },
      {
        slug: 'source-roi',
        title: 'Source ROI Tracking',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Know exactly which marketing channels deliver results — and which ones waste budget.' },
          { type: 'heading', level: 2, text: 'Reading the Source ROI report', id: 'reading-report' },
          { type: 'paragraph', text: 'The Source ROI report shows, for each lead source:' },
          { type: 'list', items: [
            'Total leads captured from this source',
            'Conversion rate (leads that reached "Enrolled" stage)',
            'Average time to convert',
            'Revenue generated from converted leads',
            'Cost per lead (if you\'ve entered your ad spend)',
            'ROI percentage',
          ]},
          { type: 'heading', level: 2, text: 'Acting on the data', id: 'acting-on-data' },
          { type: 'list', items: [
            'Double down on high-ROI sources — if referrals convert at 40% vs Meta Ads at 8%, invest in referral programs',
            'Fix leaky sources — if Google Ads brings leads but they drop off at "Demo Scheduled", the issue might be demo quality, not the ad channel',
            'Kill underperformers — if a source consistently brings low-quality leads with <5% conversion, reallocate that budget',
          ]},
          { type: 'tip', text: 'Review Source ROI monthly. Share results with your marketing team or agency so they can optimize campaigns based on actual conversion data, not just click metrics.' },
        ],
      },
      {
        slug: 'counselor-performance',
        title: 'Counselor Performance',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Track how your counselors are performing and identify coaching opportunities.' },
          { type: 'heading', level: 2, text: 'Performance metrics', id: 'performance-metrics' },
          { type: 'list', items: [
            'Leads assigned vs converted — raw conversion rate per counselor',
            'Average response time — how quickly they contact new leads',
            'Follow-up compliance — percentage of scheduled follow-ups completed on time',
            'Pipeline velocity — average time from lead assignment to enrollment',
            'Activity volume — calls, messages, and notes logged per day/week',
          ]},
          { type: 'heading', level: 2, text: 'Using the leaderboard', id: 'using-leaderboard' },
          { type: 'paragraph', text: 'The counselor leaderboard ranks team members by conversion rate. Use it to:' },
          { type: 'list', items: [
            'Recognize top performers — share the leaderboard in team meetings',
            'Identify training needs — counselors with high lead volume but low conversion may need scripting help',
            'Balance workloads — if one counselor is overloaded, redistribute leads to maintain quality',
          ]},
          { type: 'warning', text: 'Don\'t evaluate counselors on conversion rate alone. A counselor handling difficult sources (cold ads) will naturally have lower rates than one handling walk-ins. Compare within similar lead pools.' },
        ],
      },
      {
        slug: 'revenue-reports',
        title: 'Revenue Reports',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Track collections, outstanding, and revenue trends across your institute.' },
          { type: 'heading', level: 2, text: 'Key revenue views', id: 'key-views' },
          { type: 'list', items: [
            'Total collected vs outstanding — the headline numbers for your institute',
            'Revenue by branch — compare collection performance across locations',
            'Revenue by course — see which courses generate the most revenue',
            'Revenue by period — monthly, quarterly, or custom date range trends',
            'Payment mode breakdown — UPI vs cash vs bank transfer distribution',
          ]},
          { type: 'heading', level: 2, text: 'Generating reports', id: 'generating-reports' },
          { type: 'steps', items: [
            'Go to Reports from the sidebar',
            'Select "Revenue" from the report type dropdown',
            'Choose your filters: branch, course, date range',
            'The report generates instantly with charts and summary tables',
            'Click "Export" to download as PDF or CSV',
          ]},
          { type: 'tip', text: 'Set up a monthly routine: export revenue reports on the 1st of each month and share with your accountant. This keeps your books aligned with actual collections.' },
        ],
      },
      {
        slug: 'exporting-data',
        title: 'Exporting Data',
        type: 'detail',
        readTime: '3 min',
        content: [
          { type: 'paragraph', text: 'Every report and data view in Admitrix can be exported for offline use, accounting, or presentations.' },
          { type: 'heading', level: 2, text: 'Export formats', id: 'export-formats' },
          { type: 'list', items: [
            'CSV — Raw data for Excel, Google Sheets, or your accounting software',
            'PDF — Formatted reports ready for printing or sharing',
          ]},
          { type: 'heading', level: 2, text: 'What you can export', id: 'what-to-export' },
          { type: 'list', items: [
            'Lead lists — Filtered by any criteria (stage, source, branch, date)',
            'Fee reports — Collections, outstanding, payment history',
            'Analytics reports — Source ROI, counselor performance, pipeline metrics',
            'Audit logs — Activity history for compliance documentation',
            'Invoice records — Individual or batch invoice exports',
          ]},
          { type: 'heading', level: 2, text: 'Exporting', id: 'how-to-export' },
          { type: 'steps', items: [
            'Navigate to any data view or report',
            'Apply your desired filters',
            'Click the "Export" button in the toolbar',
            'Select format: CSV or PDF',
            'The file downloads immediately',
          ]},
          { type: 'heading', level: 2, text: 'Report page features', id: 'report-features' },
          { type: 'list', items: [
            'Date range filters — All report pages support custom date range filtering to narrow results to the period you need',
            'Source ROI table — Shows conversion rate and revenue per source, sortable by any column',
            'Counselor performance table — Sortable by conversion rate, response time, leads assigned, and more',
          ]},
          { type: 'tip', text: 'For recurring exports (e.g., monthly fee reports for your CA), use automation to schedule exports and email them automatically.' },
        ],
      },
    ],
  },
  {
    id: 'campaigns',
    title: 'Marketing & Campaigns',
    icon: Megaphone,
    color: '#EC4899',
    description: 'Track campaigns, attribution, and marketing performance',
    articles: [
      {
        slug: 'overview',
        title: 'Marketing & Campaigns Overview',
        type: 'overview',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Admitrix tracks every lead\'s full marketing attribution chain — which campaign brought them in, what ad they clicked, what UTM tags they had, and how they converted. Compare campaigns, platforms, and ad creatives to find what actually drives enrollments.' },
          { type: 'heading', level: 2, text: 'What gets tracked', id: 'what-tracked' },
          { type: 'list', items: [
            'Campaign name and external campaign ID',
            'Ad group / ad set',
            'Ad ID and creative',
            'Google Click ID (GCLID) for Google Ads',
            'Facebook Click ID (FBCLID) for Meta Ads',
            'UTM parameters: source, medium, campaign, term, content',
            'Landing page URL and referrer',
            'City and device type',
            'Touchpoint history when leads contact you multiple times',
          ]},
          { type: 'heading', level: 2, text: 'Why it matters', id: 'why' },
          { type: 'list', items: [
            'Stop wasting budget on campaigns that bring leads but don\'t convert',
            'Identify your best performing platforms (Meta vs Google vs walk-in)',
            'See which ad creatives drive enrollments, not just clicks',
            'Catch out-of-location leads from poorly targeted ads',
            'Compare cost-per-lead and cost-per-enrollment across campaigns',
            'Calculate true ROAS based on actual revenue, not vanity metrics',
          ]},
          { type: 'tip', text: 'For best results, create a campaign in Admitrix for every paid campaign you run. Use the same external campaign ID as your Meta or Google Ads platform so leads auto-link.' },
        ],
      },
      {
        slug: 'creating-campaigns',
        title: 'Creating Campaigns',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'A campaign in Admitrix represents one of your marketing initiatives — a Meta ad campaign, a Google Ads campaign, an offline event, a referral drive, etc.' },
          { type: 'heading', level: 2, text: 'Adding a new campaign', id: 'adding-campaign' },
          { type: 'steps', items: [
            'Go to Campaigns from the sidebar (admin or branch manager access)',
            'Click "+ New Campaign"',
            'Fill in the campaign name (e.g., "JEE 2027 Meta Lead Form")',
            'Enter the external campaign ID — this is the ID from your ad platform (Meta Ads Manager or Google Ads). Leads with this ID will auto-link.',
            'Select the source (meta_ads, google_ads, etc.)',
            'Specify the platform (e.g., Facebook, Instagram, Google Search)',
            'Enter your budget and target cost-per-lead (CPL)',
            'Set start and end dates',
            'Choose the branch this campaign drives leads for',
            'Set status to Active',
            'Click "Create Campaign"',
          ]},
          { type: 'tip', text: 'The external campaign ID is critical — it\'s the link between your ad platform and Admitrix. Find it in Meta Ads Manager URL or Google Ads Editor.' },
          { type: 'heading', level: 2, text: 'Campaign fields explained', id: 'fields' },
          { type: 'list', items: [
            'Name — Internal name for your team (e.g., "Summer JEE Drive")',
            'Campaign ID — External platform ID (e.g., Meta campaign 23847562394)',
            'Source — Lead source category for grouping',
            'Platform — Specific platform (Facebook, Instagram, Google Search, etc.)',
            'Budget — Total allocated budget in INR',
            'Spend — Actual amount spent (update manually as the campaign runs)',
            'Target CPL — What you aim to spend per lead',
            'Branch — Which branch this campaign generates leads for',
            'Status — Active, Paused, Completed, or Draft',
          ]},
        ],
      },
      {
        slug: 'utm-tracking',
        title: 'UTM Parameters & Click IDs',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'UTM parameters and click IDs (GCLID, FBCLID) are the foundation of accurate attribution. They tell Admitrix exactly which ad, campaign, and platform sent each lead.' },
          { type: 'heading', level: 2, text: 'What is a UTM parameter?', id: 'what-is-utm' },
          { type: 'paragraph', text: 'UTM parameters are tags appended to URLs that track marketing source data. When someone clicks an ad with UTM parameters and lands on your form, those parameters are captured automatically.' },
          { type: 'paragraph', text: 'Example URL with UTMs:' },
          { type: 'paragraph', text: 'https://yoursite.com/?utm_source=facebook&utm_medium=cpc&utm_campaign=jee2027&utm_content=video_ad_v2' },
          { type: 'heading', level: 2, text: 'The five UTM parameters', id: 'five-utms' },
          { type: 'list', items: [
            'utm_source — Where the traffic came from (facebook, google, newsletter)',
            'utm_medium — The marketing medium (cpc, organic, email, paid_social)',
            'utm_campaign — Your campaign identifier (jee2027_summer_drive)',
            'utm_term — Paid search keyword (best_jee_coaching_pune)',
            'utm_content — Specific ad creative (video_v1, banner_red, headline_test_a)',
          ]},
          { type: 'heading', level: 2, text: 'Click IDs', id: 'click-ids' },
          { type: 'list', items: [
            'GCLID (Google Click ID) — Auto-generated by Google Ads on every ad click. Captured automatically.',
            'FBCLID (Facebook Click ID) — Auto-generated by Meta Ads on every ad click. Captured automatically.',
          ]},
          { type: 'paragraph', text: 'Click IDs are more reliable than UTM parameters because they can\'t be stripped or modified. Use them for advanced attribution and offline conversion tracking.' },
          { type: 'heading', level: 2, text: 'Setting up UTM tracking on your ads', id: 'setup' },
          { type: 'steps', items: [
            'In Google Ads, enable auto-tagging (adds GCLID automatically)',
            'In Meta Ads Manager, set URL parameters in your campaign settings',
            'For organic posts, manually add UTM parameters to your bio links',
            'Use a UTM builder tool (Google has a free one) to construct URLs',
            'Test by clicking your own ad and checking the captured fields on the new lead',
          ]},
          { type: 'warning', text: 'Don\'t use spaces or special characters in UTM values. Use underscores or dashes. Always use lowercase for consistency.' },
        ],
      },
      {
        slug: 'campaign-performance',
        title: 'Campaign Performance Reports',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'The Campaign Performance report is where you decide which campaigns to scale, pause, or kill. Every campaign is scored on real conversion data — not vanity metrics.' },
          { type: 'heading', level: 2, text: 'Where to find it', id: 'where' },
          { type: 'paragraph', text: 'Go to Reports > Campaigns tab.' },
          { type: 'heading', level: 2, text: 'Key metrics explained', id: 'metrics' },
          { type: 'list', items: [
            'Total Leads — Every lead attributed to this campaign',
            'Unique Leads — Excluding duplicate touchpoints',
            'Qualified — Leads that progressed past initial inquiry',
            'Enrolled — Leads that became paying students',
            'Conversion Rate — Enrolled / Total Leads × 100',
            'Spend — Amount spent on this campaign (manually updated)',
            'Revenue — Actual fee revenue from enrolled leads',
            'CPL (Cost Per Lead) — Spend / Total Leads',
            'CPE (Cost Per Enrollment) — Spend / Enrolled',
            'ROAS (Return on Ad Spend) — Revenue / Spend',
          ]},
          { type: 'heading', level: 2, text: 'Reading the data', id: 'reading' },
          { type: 'paragraph', text: 'Sort by ROAS to see your most profitable campaigns. Sort by CPE to see which campaigns deliver enrollments most efficiently. Click any row to see the full funnel breakdown for that campaign.' },
          { type: 'heading', level: 2, text: 'Acting on the data', id: 'acting' },
          { type: 'list', items: [
            'ROAS > 3x — Scale this campaign, increase budget',
            'ROAS 1-3x — Optimize creative or targeting',
            'ROAS < 1x — Pause and rework, you\'re losing money',
            'High CPL but high enrollment rate — Lead is expensive but converts well, keep running',
            'Low CPL but low enrollment rate — Cheap clicks but bad fit, refine targeting',
            'High duplicates — Same audience hitting you multiple times, adjust frequency cap',
          ]},
          { type: 'tip', text: 'Review campaign performance weekly. Pause underperformers within 2 weeks of launch — don\'t let bad campaigns burn through budget.' },
        ],
      },
      {
        slug: 'platform-comparison',
        title: 'Platform Comparison',
        type: 'detail',
        readTime: '3 min',
        content: [
          { type: 'paragraph', text: 'Beyond individual campaigns, you need to know which platforms (Meta vs Google vs Walk-in vs Referral) are working overall.' },
          { type: 'heading', level: 2, text: 'Where to find it', id: 'where' },
          { type: 'paragraph', text: 'Reports > Campaigns tab. The Platform Breakdown pie chart at the top shows lead distribution across all sources.' },
          { type: 'heading', level: 2, text: 'What to compare', id: 'compare' },
          { type: 'list', items: [
            'Lead volume per platform — Where most leads come from',
            'Conversion rate per platform — Quality of leads from each source',
            'Total revenue per platform — Bottom line impact',
            'Time to enrollment — How long leads from each platform take to convert',
          ]},
          { type: 'heading', level: 2, text: 'Common patterns', id: 'patterns' },
          { type: 'list', items: [
            'Meta Ads usually has high volume, lower conversion — good for awareness',
            'Google Ads usually has lower volume, higher conversion — high intent',
            'Referrals usually have the highest conversion rate but lowest volume',
            'Walk-ins convert best but require offline marketing investment',
          ]},
          { type: 'tip', text: 'Don\'t put all your budget on one platform. The best ad strategies use 2-3 platforms to diversify risk and capture different customer segments.' },
        ],
      },
      {
        slug: 'out-of-location',
        title: 'Out-of-Location Leads',
        type: 'detail',
        readTime: '3 min',
        content: [
          { type: 'paragraph', text: 'Out-of-location leads are inquiries from cities that don\'t match your branch locations. They\'re usually a sign of poorly targeted ads — you\'re paying for leads that will never enroll because they\'re too far away.' },
          { type: 'heading', level: 2, text: 'Where to find them', id: 'where' },
          { type: 'paragraph', text: 'Reports > Out of Location tab. The page shows every lead where the captured city differs from their assigned branch\'s city.' },
          { type: 'heading', level: 2, text: 'Why this matters', id: 'why' },
          { type: 'list', items: [
            'You\'re paying for leads that can\'t physically attend your centre',
            'Counselors waste time on leads that won\'t convert',
            'These leads inflate your top-line metrics but tank your conversion rate',
            'Your ad targeting might be misconfigured (showing ads in wrong cities)',
          ]},
          { type: 'heading', level: 2, text: 'How to fix it', id: 'fix' },
          { type: 'steps', items: [
            'Identify which campaigns are bringing out-of-location leads',
            'Go to your ad platform and tighten geographic targeting',
            'For Meta Ads, set location targeting to "People living in this location" (not "Recently in")',
            'For Google Ads, exclude regions where you don\'t have a branch',
            'Re-check the Out-of-Location report after a week to confirm improvement',
          ]},
          { type: 'warning', text: 'Some out-of-location leads are legitimate — students looking to relocate or take online classes. Don\'t auto-reject them. Manually review before transferring or marking as lost.' },
        ],
      },
      {
        slug: 'duplicate-detection',
        title: 'Duplicate Lead Detection',
        type: 'detail',
        readTime: '3 min',
        content: [
          { type: 'paragraph', text: 'When the same person fills out multiple forms or comes back through different campaigns, Admitrix detects the duplicate and keeps both records linked. This preserves attribution data so you know every touchpoint that contributed.' },
          { type: 'heading', level: 2, text: 'How detection works', id: 'how' },
          { type: 'paragraph', text: 'Admitrix matches duplicates by phone number. When a new lead comes in with a phone that already exists:' },
          { type: 'list', items: [
            'A new "duplicate" record is created with the new attribution data',
            'The duplicate is linked to the original via the "duplicate_of" field',
            'A touchpoint entry is added to the original lead\'s history',
            'The duplicate doesn\'t enter the active pipeline (counselor isn\'t spammed)',
            'The original lead\'s record is updated to reflect the new touchpoint',
          ]},
          { type: 'heading', level: 2, text: 'Viewing touchpoints', id: 'touchpoints' },
          { type: 'paragraph', text: 'Open any lead\'s profile. The Attribution card on the right shows all touchpoints — every time this lead engaged with your marketing across campaigns, sources, and dates.' },
          { type: 'heading', level: 2, text: 'Why this matters', id: 'why' },
          { type: 'list', items: [
            'Multi-touch attribution — see the full journey, not just the last click',
            'Avoid double-counting leads in your reports',
            'Identify which campaigns first introduced a lead vs which converted them',
            'Stop sending duplicate WhatsApp messages to confused parents',
          ]},
          { type: 'tip', text: 'A lead with 3+ touchpoints across campaigns is highly engaged. Prioritize them for your best counselors.' },
        ],
      },
      {
        slug: 'website-form-integration',
        title: 'Website Form Integration',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'To capture UTM parameters and attribution from your website forms, you need to pass the marketing data along with the form submission to Admitrix\'s webhook.' },
          { type: 'heading', level: 2, text: 'Step 1: Capture UTMs on your landing page', id: 'capture' },
          { type: 'paragraph', text: 'Add this JavaScript to your landing page to read UTM parameters from the URL and store them in cookies or localStorage:' },
          { type: 'paragraph', text: 'When the form is submitted, read these stored values and include them in the webhook payload.' },
          { type: 'heading', level: 2, text: 'Step 2: POST to the webhook', id: 'webhook' },
          { type: 'paragraph', text: 'Send a POST request to https://yourdomain.com/api/webhooks/generic with this JSON body:' },
          { type: 'list', items: [
            'student_name (required)',
            'parent_phone (required)',
            'email, parent_name, source, branch_id, notes',
            'utm_source, utm_medium, utm_campaign, utm_term, utm_content',
            'gclid, fbclid',
            'campaign_id (if you know which campaign drove the visit)',
            'referrer_url, landing_page, city, device_type',
          ]},
          { type: 'heading', level: 2, text: 'Step 3: Test the integration', id: 'test' },
          { type: 'steps', items: [
            'Visit your landing page with test UTM parameters in the URL',
            'Submit the form with test data',
            'Check Admitrix Leads to confirm the lead was created',
            'Open the lead profile and verify the Attribution card shows your test data',
          ]},
          { type: 'tip', text: 'If you use Zapier, you can connect Google Forms, Typeform, or any other form builder to the generic webhook without writing code. Just map the form fields to the webhook fields.' },
        ],
      },
    ],
  },
  {
    id: 'integrations',
    title: 'Integrations',
    icon: Lightning,
    color: '#F97316',
    description: 'Connect Admitrix with payment gateways, ad platforms, and more',
    articles: [
      {
        slug: 'overview',
        title: 'Integrations Overview',
        type: 'overview',
        readTime: '3 min',
        content: [
          { type: 'paragraph', text: 'Admitrix connects with the tools your institute already uses — payment gateways, ad platforms, messaging services, calendars, and custom systems.' },
          { type: 'heading', level: 2, text: 'Available integrations', id: 'available' },
          { type: 'list', items: [
            'Razorpay — Accept online payments via UPI, cards, netbanking, and wallets',
            'Meta Lead Ads — Auto-capture leads from Facebook and Instagram ad campaigns',
            'MSG91 — Send DLT-compliant SMS messages to parents and students',
            'SendGrid — Professional email delivery for receipts, confirmations, and campaigns',
            'Google Calendar — Schedule and sync demos, follow-ups, and batch sessions',
            'Generic Webhook — Connect website forms, Zapier, IndiaMART, JustDial, or any custom system',
          ]},
          { type: 'heading', level: 2, text: 'Configuration', id: 'configuration' },
          { type: 'paragraph', text: 'Integrations are configured via environment variables by your system administrator. Each integration works independently — enable only what you need. All integrations gracefully fall back to logging when not configured.' },
        ],
      },
      {
        slug: 'razorpay',
        title: 'Razorpay Payment Gateway',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Accept online fee payments from parents via Razorpay. Supports UPI, credit/debit cards, netbanking, and wallets.' },
          { type: 'heading', level: 2, text: 'Setup', id: 'setup' },
          { type: 'steps', items: [
            'Sign up at razorpay.com and complete KYC verification',
            'Go to Settings > API Keys in your Razorpay dashboard',
            'Copy the Key ID and Key Secret',
            'Set RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET in your server environment',
            'Restart the backend — online payments are now active',
          ]},
          { type: 'heading', level: 2, text: 'How payments work', id: 'how-it-works' },
          { type: 'list', items: [
            'Staff clicks "Pay Online" on an unpaid invoice',
            'Razorpay checkout popup opens with the outstanding amount',
            'Parent completes payment via their preferred method',
            'Payment is cryptographically verified and auto-recorded',
            'Invoice status updates instantly, receipt email sent if configured',
          ]},
          { type: 'heading', level: 2, text: 'Test vs Live mode', id: 'test-vs-live' },
          { type: 'paragraph', text: 'Razorpay provides test API keys for development. Test mode simulates payments without real money. When ready, swap to live keys — no code changes needed.' },
          { type: 'tip', text: 'Set up a Razorpay webhook (POST /api/payments/razorpay/webhook) for reliable async payment confirmation, especially for UPI payments that may take a few seconds.' },
        ],
      },
      {
        slug: 'meta-lead-ads',
        title: 'Meta (Facebook/Instagram) Lead Ads',
        type: 'detail',
        readTime: '5 min',
        content: [
          { type: 'paragraph', text: 'Automatically capture leads from your Facebook and Instagram ad campaigns directly into Admitrix. No manual entry, no CSV downloads, no delays.' },
          { type: 'heading', level: 2, text: 'Setup', id: 'setup' },
          { type: 'steps', items: [
            'Go to Meta Business Suite > Business Settings > Leads Access',
            'Select your page and add a webhook URL: https://yourdomain.com/api/webhooks/meta/leads',
            'Use the verify token: your META_VERIFY_TOKEN environment variable',
            'Subscribe to the "leadgen" field',
            'Test by submitting a test lead from Meta\'s webhook testing tool',
          ]},
          { type: 'heading', level: 2, text: 'What happens when a lead comes in', id: 'lead-flow' },
          { type: 'list', items: [
            'Parent fills out your Facebook/Instagram lead form',
            'Meta sends the lead data to Admitrix via webhook',
            'Admitrix creates a new lead with source "meta_ads"',
            'Lead is routed to the default branch (or specified branch)',
            'Auto-assignment picks a counselor via round-robin',
            'Lead score is calculated automatically',
            'Automation rules fire (e.g., send welcome WhatsApp)',
            'Assigned counselor receives a notification',
          ]},
          { type: 'paragraph', text: 'Duplicate leads (same phone number) are automatically skipped.' },
          { type: 'tip', text: 'Combine Meta Lead Ads with an automation rule that sends an instant WhatsApp welcome message. Response time under 5 minutes dramatically increases conversion rates.' },
        ],
      },
      {
        slug: 'webhooks',
        title: 'Generic Webhook API',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Connect any external system — website contact forms, Zapier, IndiaMART, JustDial, or custom applications — to automatically create leads in Admitrix.' },
          { type: 'heading', level: 2, text: 'Endpoint', id: 'endpoint' },
          { type: 'paragraph', text: 'POST https://yourdomain.com/api/webhooks/generic' },
          { type: 'heading', level: 2, text: 'Request format', id: 'request-format' },
          { type: 'paragraph', text: 'Send a JSON body with these fields:' },
          { type: 'list', items: [
            'student_name (required) — Student\'s full name',
            'parent_phone or phone (required) — 10-digit phone number',
            'parent_name — Parent\'s name (defaults to student_name)',
            'email — Email address',
            'source — Lead source label (defaults to "website")',
            'branch_id — Target branch (defaults to your default branch setting)',
            'notes — Additional notes',
          ]},
          { type: 'heading', level: 2, text: 'Authentication', id: 'authentication' },
          { type: 'paragraph', text: 'Set the WEBHOOK_API_KEY environment variable and pass it as an X-API-Key header or api_key query parameter. If no key is configured, the endpoint is open (suitable for internal use only).' },
          { type: 'tip', text: 'Use Zapier to connect your website form (Google Forms, Typeform, etc.) to this webhook. No coding required — just map the form fields to the JSON fields above.' },
        ],
      },
      {
        slug: 'google-calendar',
        title: 'Google Calendar',
        type: 'detail',
        readTime: '4 min',
        content: [
          { type: 'paragraph', text: 'Schedule demos, follow-ups, and batch sessions from Admitrix and sync them to Google Calendar. Events appear on your team\'s calendars with reminders.' },
          { type: 'heading', level: 2, text: 'Setup', id: 'setup' },
          { type: 'steps', items: [
            'Create a Google Cloud project and enable the Calendar API',
            'Create a service account and download the JSON credentials',
            'Share your Google Calendar with the service account email (give "Make changes to events" permission)',
            'Set GOOGLE_CALENDAR_CREDENTIALS (the full JSON string) and GOOGLE_CALENDAR_ID in your environment',
          ]},
          { type: 'heading', level: 2, text: 'Scheduling a demo', id: 'scheduling-demo' },
          { type: 'steps', items: [
            'Open a lead\'s profile page',
            'Click "Schedule Demo" in the header',
            'Set the date, time, and optional location',
            'The attendee email auto-fills from the lead\'s email',
            'Click "Schedule" — the event appears on Google Calendar and on the lead\'s timeline',
          ]},
          { type: 'heading', level: 2, text: 'Upcoming events', id: 'upcoming' },
          { type: 'paragraph', text: 'The Dashboard shows your upcoming events for the next 7 days. Counselors see their own scheduled events, while managers and admins see all events.' },
          { type: 'tip', text: 'If Google Calendar is not configured, events are still stored in Admitrix and visible on lead profiles and the dashboard — they just won\'t sync to Google.' },
        ],
      },
      {
        slug: 'sms-msg91',
        title: 'SMS via MSG91',
        type: 'detail',
        readTime: '3 min',
        content: [
          { type: 'paragraph', text: 'Send real SMS messages to parents and students via MSG91. Fully DLT-compliant for Indian telecom regulations.' },
          { type: 'heading', level: 2, text: 'Setup', id: 'setup' },
          { type: 'steps', items: [
            'Sign up at msg91.com',
            'Complete DLT registration for your sender ID',
            'Get your auth key from MSG91 dashboard',
            'Set MSG91_AUTH_KEY and MSG91_SENDER_ID in your environment',
          ]},
          { type: 'paragraph', text: 'When not configured, SMS messages are logged but not delivered. This is useful for development and testing.' },
          { type: 'tip', text: 'DLT registration is mandatory in India for sending SMS. Apply for your sender ID and message templates through your telecom operator\'s DLT portal.' },
        ],
      },
      {
        slug: 'email-sendgrid',
        title: 'Email via SendGrid',
        type: 'detail',
        readTime: '3 min',
        content: [
          { type: 'paragraph', text: 'Admitrix sends professional emails via SendGrid — fee receipts, admission confirmations, password resets, and custom messages.' },
          { type: 'heading', level: 2, text: 'Setup', id: 'setup' },
          { type: 'steps', items: [
            'Sign up at sendgrid.com (free tier: 100 emails/day)',
            'Create an API key with "Mail Send" permissions',
            'Verify your sender email address or domain',
            'Set SENDGRID_API_KEY, SENDGRID_FROM_EMAIL, and SENDGRID_FROM_NAME in your environment',
          ]},
          { type: 'heading', level: 2, text: 'Automatic emails', id: 'automatic-emails' },
          { type: 'paragraph', text: 'Once configured, Admitrix automatically sends:' },
          { type: 'list', items: [
            'Fee receipt emails when a payment is recorded',
            'Admission confirmation when a lead is enrolled',
            'Password reset links when requested',
          ]},
          { type: 'paragraph', text: 'When not configured, emails are logged but not delivered.' },
          { type: 'tip', text: 'Verify your domain (not just your email) in SendGrid for better deliverability and to avoid spam filters.' },
        ],
      },
    ],
  },
];

// Helper functions
export function getAllArticles() {
  const articles = [];
  DOCS_CATEGORIES.forEach(cat => {
    cat.articles.forEach((article, index) => {
      articles.push({ ...article, categoryId: cat.id, categoryTitle: cat.title, categoryColor: cat.color, index });
    });
  });
  return articles;
}

export function getCategory(categoryId) {
  return DOCS_CATEGORIES.find(c => c.id === categoryId) || null;
}

export function getArticle(categoryId, slug) {
  const cat = getCategory(categoryId);
  if (!cat) return null;
  const articleIndex = cat.articles.findIndex(a => a.slug === slug);
  if (articleIndex === -1) return null;
  const article = cat.articles[articleIndex];

  // Compute prev/next within category
  const prev = articleIndex > 0 ? { slug: cat.articles[articleIndex - 1].slug, title: cat.articles[articleIndex - 1].title, categoryId: cat.id } : null;
  const next = articleIndex < cat.articles.length - 1 ? { slug: cat.articles[articleIndex + 1].slug, title: cat.articles[articleIndex + 1].title, categoryId: cat.id } : null;

  return { ...article, categoryId: cat.id, categoryTitle: cat.title, categoryColor: cat.color, prev, next };
}

export function searchArticles(query) {
  if (!query || query.length < 2) return [];
  const lower = query.toLowerCase();
  return getAllArticles().filter(a =>
    a.title.toLowerCase().includes(lower) ||
    a.categoryTitle.toLowerCase().includes(lower)
  );
}
